import { Component, ViewChild } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Searchbar } from 'ionic-angular';
import { OperationDetailsPage } from '../operation-details/operation-details';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { SearchService, SearchTextPart } from '../../../services/search/search.service';
import {
  DailyOperationLogRecord,
  DprSplitOperations,
  OperationType,
  ProjectSpecificOperationSummary,
  Stage,
  StageSplit
} from '../../../models/projects/operation.models';
import { DailyLogModalPage } from '../../daily-log/daily-log-modal/daily-log-modal';
import { SetupService } from '../../../services/setup/setup.service';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { StageService } from '../../../services/stage/stage.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { AppConfig } from '../../../app/app.config';
import { SetupDataService } from '../../../services/setup/setup-data.service';

@IonicPage()
@Component({
  selector: 'page-select-operation',
  templateUrl: 'select-operation.html',
})
export class SelectOperationPage {
  @ViewChild('operationSearchbar') operationSearchbar: Searchbar;
  config = AppConfig.settings;
  stageSplitOperations: StageSplit[] = [];
  record: DailyOperationLogRecord;
  visibleOperations: ProjectSpecificOperationSummary[] = [];
  searchHighlights = new Map<ProjectSpecificOperationSummary, {
    name: SearchTextPart[];
    start: SearchTextPart[];
    end: SearchTextPart[];
    snippet?: { parts: SearchTextPart[]; title: string; additionalCount: number; isStep: boolean };
  }>();
  highlightedOperationIndex: number = -1;
  lastSearchInput: string = '';

  searchInput: string = '';

  type: OperationType = OperationType.Contractual;

  contractual = OperationType.Contractual;
  concurrent = OperationType.Concurrent;
  delay = OperationType.Delay;
  interruption = OperationType.Interruption;
  loading: boolean;
  dailyLogDate: PtLuxonDate;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public operationService: OperationService,
    private setupService: SetupService,
    private setupDataService: SetupDataService,
    private navHelperService: NavHelperService,
    public modalController: ModalController,
    private dailyLogService: DailyLogService,
    private stageService: StageService,
    private searchService: SearchService,
  ) {
    this.type = navParams.get('type');
  }

  ngOnInit() {
    this.navHelperService.updateSelectOperationRequiresReload(true);
  }

  async ionViewDidEnter() {
    // Focus the search box after navigation settles so users can type immediately.
    setTimeout(() => this.operationSearchbar && this.operationSearchbar.setFocus(), 150);

    // if user was last on the operation edit screen and used the back button, then there's no need to load again
    // if they clicked the select op directly form the menu we still need to load
    let reloadPage = true;
    this.navHelperService.selectOperationRequiresReload.subscribe(requiresReload => {
      reloadPage = requiresReload;
    });

    if (!reloadPage) {
      return;
    }

    this.loading = true;
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    const setupData = await this.getSetupDataForOperationLoad();
    const stageList = await this.operationService.getStageIdsForDay(this.dailyLogDate, setupData);

    if (stageList && stageList.length > 0) {
      const operationMetadata = await this.operationService.getOperationsMetadata(this.dailyLogDate, setupData);
      if (operationMetadata && operationMetadata.projectSpecificOperations) {
        await this.operationService.saveOperationsData(operationMetadata);
      }
    }

    this.operationService.getOperations()
      .then(async operations => {
        this.stageSplitOperations = [];

        if (!!operations) {
          operations.forEach(element => {
            if (!element.name && !!element.operationSummary) {
              element.name = element.operationSummary.name;
            }
          });
        }

        operations.sort((first, second) => first.name.localeCompare((second.name)));

        this.stageService
          .getSummaries(stageList)
          .then(stages => {
            switch (this.type) {
              case OperationType.Contractual:
                operations = operations
                  .filter(p => !p.forbidContractual && p.operationSummary.classificationName !== 'Delay'
                    && p.operationSummary.classificationName !== 'Interruption');
                this.filterByPSO(operations, stages);
                break;
              case OperationType.Concurrent:
                operations = operations
                  .filter(p => p.defaultIsConcurrentOperation && p.operationSummary.classificationName !== 'Delay'
                    && p.operationSummary.classificationName !== 'Interruption');
                this.filterByClassification(operations, stages);
                break;
              case OperationType.Delay:
                operations = operations
                  .filter(p => p.operationSummary.classificationName === 'Delay');
                this.filterBySubClassification(operations, stages);
                break;
              case OperationType.Interruption:
                operations = operations
                  .filter(p => p.isInterruption);
                this.filterBySubClassification(operations, stages);
                break;
              default:
                break;
            }

            this.search();
            this.loading = false;
          });
      });

    this.navHelperService.updateSelectOperationRequiresReload(false);
  }

  private async getSetupDataForOperationLoad() {
    let setupData = await this.setupDataService.getSetupChangeLogData();
    const hasStageData = setupData.some(record => record.changeTypeId === 3);

    if (!hasStageData) {
      await this.setupService.updateSetupChangeLogDataForDate(this.dailyLogDate.dateTime);
      setupData = await this.setupDataService.getSetupChangeLogData();
    }

    return setupData;
  }

  filterByPSO(projectSpecificOperations: ProjectSpecificOperationSummary[], stages: Stage[]) {
    const stageList = projectSpecificOperations
      .map(p => p.stageIds.find(f => true))
      .filter((id, index, array) => array.indexOf(id) === index);

    this.stageSplitOperations = [];

    for (const stageId of stageList) {
      const stageSplit = new StageSplit();

      const stageOperations = projectSpecificOperations
        .filter(p => p.stageIds.find(f => true) === stageId);

      const dprList = stageOperations
        .map(p => p.dprContractCategoryTag)
        .filter((dprTag, index, array) => array.indexOf(dprTag) === index);

      dprList.forEach(element => {
        const dprOperations = stageOperations
          .filter(p => p.dprContractCategoryTag === element);

        const newDpr = new DprSplitOperations();
        newDpr.dprContract = element;
        newDpr.operations = dprOperations;
        stageSplit.dprSplitOperations.push(newDpr);
      });

      stageSplit.stageId = stageId;
      const stage = stages.find(p => p.id === stageId);

      if (stage != null) {
        stageSplit.stageName = stage.name;
        stageSplit.phaseName = stage.phaseName;
        stageSplit.projectName = stage.projectName;
      }

      this.stageSplitOperations.push(stageSplit);
    }
  }

  hideStageOperations(stageSplit: StageSplit) {
    stageSplit.hideOperations = true;
  }

  showStageOperations(stageSplit: StageSplit) {
    stageSplit.hideOperations = false;
  }

  filterByClassification(projectSpecificOperations: ProjectSpecificOperationSummary[], stages: Stage[]) {
    const stageList = projectSpecificOperations
      .map(p => p.stageIds.find(f => true))
      .filter((id, index, array) => array.indexOf(id) === index);

    this.stageSplitOperations = [];

    for (const stageId of stageList) {
      const stageSplit = new StageSplit();
      const stageOperations = projectSpecificOperations
        .filter(p => p.stageIds.find(f => true) === stageId);

      const subClassificationList = stageOperations
        .map(p => p.operationSummary.classificationName)
        .filter((classification, index, array) => array.indexOf(classification) === index);

      subClassificationList.forEach(element => {
        const dprOperations = stageOperations
          .filter(p => p.operationSummary.classificationName === element);

        const newDpr = new DprSplitOperations();
        newDpr.dprContract = element;
        newDpr.operations = dprOperations;
        stageSplit.dprSplitOperations.push(newDpr);
      });

      stageSplit.stageId = stageId;
      stageSplit.stageName = stages.find(p => p.id === stageId).name;
      stageSplit.phaseName = stages.find(p => p.id === stageId).phaseName;
      stageSplit.projectName = stages.find(p => p.id === stageId).projectName;
      this.stageSplitOperations.push(stageSplit);
    }
  }

  filterBySubClassification(projectSpecificOperations: ProjectSpecificOperationSummary[], stages: Stage[]) {
    const stageList = projectSpecificOperations
      .map(p => p.stageIds.find(f => true))
      .filter((id, index, array) => array.indexOf(id) === index);

    this.stageSplitOperations = [];

    for (const stageId of stageList) {
      const stageSplit = new StageSplit();
      const stageOperations = projectSpecificOperations
        .filter(p => p.stageIds.find(f => true) === stageId);

      const subClassificationList = stageOperations
        .map(p => p.operationSummary.subClassificationName)
        .filter((classification, index, array) => array.indexOf(classification) === index);

      subClassificationList.forEach(element => {
        const dprOperations = stageOperations
          .filter(p => p.operationSummary.subClassificationName === element);

        const newDpr = new DprSplitOperations();
        newDpr.dprContract = element;
        newDpr.operations = dprOperations;
        stageSplit.dprSplitOperations.push(newDpr);
      });

      stageSplit.stageId = stageId;
      stageSplit.stageName = stages.find(p => p.id === stageId).name;
      stageSplit.phaseName = stages.find(p => p.id === stageId).phaseName;
      stageSplit.projectName = stages.find(p => p.id === stageId).projectName;
      this.stageSplitOperations.push(stageSplit);
    }
  }


  selectOperation(operation: ProjectSpecificOperationSummary) {
    this.navCtrl.push(OperationDetailsPage, {
      operation: operation,
      record: this.record,
      type: this.type,
      dailyLogDate: this.dailyLogDate
    });
  }

  openModal() {
    const dailyLogModal = this.modalController.create(DailyLogModalPage);
    dailyLogModal.present();
  }

  handleSearchKeydown(event: KeyboardEvent) {
    if (!this.visibleOperations.length) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedOperationIndex = this.highlightedOperationIndex < this.visibleOperations.length - 1
          ? this.highlightedOperationIndex + 1
          : 0;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedOperationIndex = this.highlightedOperationIndex > 0
          ? this.highlightedOperationIndex - 1
          : this.visibleOperations.length - 1;
        break;
      case 'Enter':
        if (this.highlightedOperationIndex >= 0) {
          event.preventDefault();
          this.selectOperation(this.visibleOperations[this.highlightedOperationIndex]);
        }
        break;
      default:
        break;
    }
  }

  isHighlightedOperation(operation: ProjectSpecificOperationSummary) {
    return this.highlightedOperationIndex >= 0
      && this.visibleOperations[this.highlightedOperationIndex] === operation;
  }

  search() {
    const searchTermChanged = this.lastSearchInput !== this.searchInput;
    this.searchHighlights.clear();

    this.stageSplitOperations.forEach(stageSplitElement => {
      let stageBestRank = Number.NEGATIVE_INFINITY;

      stageSplitElement.dprSplitOperations.forEach(dprSplitElement => {
        // Sort operations inside the current bucket by their search relevance.
        const rankedOperations = dprSplitElement.operations
          .map(operation => {
            const result = this.operationService.getOperationSearchResult(operation, this.searchInput);
            const explanation = result.explanation;
            const showSnippet = explanation && (explanation.label === 'Step' || explanation.label === 'Activity');
            this.searchHighlights.set(operation, {
              name: this.searchService.getSearchParts(operation.name || operation.operationSummary?.name, this.searchInput),
              start: this.searchService.getSearchParts(operation.operationSummary?.startDefinition, this.searchInput),
              end: this.searchService.getSearchParts(operation.operationSummary?.endDefinition, this.searchInput),
              snippet: showSnippet ? {
                isStep: explanation.label === 'Step',
                parts: this.searchService.getSearchParts(explanation.text, this.searchInput),
                title: explanation.title,
                additionalCount: explanation.additionalCount
              } : undefined
            });
            return { operation, rank: result.rank };
          })
          .sort((first, second) => {
            if (first.rank !== second.rank) {
              return second.rank - first.rank;
            }

            return (first.operation.name || first.operation.operationSummary?.name || '')
              .localeCompare(second.operation.name || second.operation.operationSummary?.name || '');
          });

        dprSplitElement.operations = rankedOperations.map(result => result.operation);

        let atleastOne = false;
        let dprBestRank = Number.NEGATIVE_INFINITY;
        rankedOperations.forEach(result => {
          const visible = result.rank > Number.NEGATIVE_INFINITY;
          result.operation.visible = visible;

          if (visible) {
            stageSplitElement.hideOperations = false;
            atleastOne = true;
            dprBestRank = Math.max(dprBestRank, result.rank);
          }
        });

        (dprSplitElement as any).__bestRank = dprBestRank;
        stageBestRank = Math.max(stageBestRank, dprBestRank);
        dprSplitElement.visible = atleastOne;
      });

      // Move the best-matching classification bucket to the top of the stage.
      stageSplitElement.dprSplitOperations.sort((first, second) => {
        const secondRank = this.getBestSearchRank(second);
        const firstRank = this.getBestSearchRank(first);
        const rankDifference = secondRank - firstRank;

        if (rankDifference !== 0) {
          return rankDifference;
        }

        return (first.dprContract || '').localeCompare(second.dprContract || '');
      });

      (stageSplitElement as any).__bestRank = stageBestRank;
    });

    // Keep stage ordering aligned with the strongest visible match on the page.
    this.stageSplitOperations.sort((first, second) => {
      const secondRank = this.getBestSearchRank(second);
      const firstRank = this.getBestSearchRank(first);
      const rankDifference = secondRank - firstRank;

      if (rankDifference !== 0) {
        return rankDifference;
      }

      return (first.stageName || '').localeCompare(second.stageName || '');
    });

    this.visibleOperations = this.buildVisibleOperationsInDisplayOrder();
    this.lastSearchInput = this.searchInput;

    if (!this.visibleOperations.length) {
      this.highlightedOperationIndex = -1;
      return;
    }

    if (searchTermChanged) {
      // Drop the old keyboard selection when a new query changes the result set.
      this.highlightedOperationIndex = -1;
      return;
    }

    if (this.highlightedOperationIndex >= this.visibleOperations.length) {
      this.highlightedOperationIndex = this.visibleOperations.length - 1;
    }
  }

  private getBestSearchRank(item: StageSplit | DprSplitOperations) {
    const rank = (item as StageSplit & DprSplitOperations & { __bestRank?: number }).__bestRank;

    return rank == null ? Number.NEGATIVE_INFINITY : rank;
  }

  private buildVisibleOperationsInDisplayOrder() {
    const visibleOperations: ProjectSpecificOperationSummary[] = [];

    // Rebuild the keyboard-navigation list from the same order that is rendered on screen.
    this.stageSplitOperations.forEach(stageSplit => {
      stageSplit.dprSplitOperations.forEach(operationSplit => {
        operationSplit.operations.forEach(operation => {
          if (operation.visible) {
            visibleOperations.push(operation);
          }
        });
      });
    });

    return visibleOperations;
  }
}



// WEBPACK FOOTER //
// ./src/pages/operations/select-operation/select-operation.ts